package com.ijoin.push

import com.ijoin.data.model.PushPlatform
import com.ijoin.data.push.PushTokenRegistrar
import kotlinx.browser.window

actual object WebPushInitializer {
    private var initialized = false
    private var messagingInstance: dynamic = null
    private var serviceWorkerRegistration: dynamic = null

    actual fun initialize() {
        if (initialized) return
        val console = window.asDynamic().console
        if (!isPushSupported()) {
            console?.warn("Web push APIs are not supported in this browser.")
            return
        }
        val firebase = window.asDynamic().firebase ?: run {
            console?.warn("Firebase scripts are not loaded; skipping web push setup.")
            return
        }
        val config = window.asDynamic().__FIREBASE_CONFIG ?: run {
            console?.warn("firebase-config.js missing; skipping web push setup.")
            return
        }
        val vapidKey = window.asDynamic().__FIREBASE_VAPID_KEY
        if (vapidKey == undefined || (vapidKey as? String).isNullOrBlank()) {
            console?.warn("WEB_PUSH_VAPID_KEY missing; skipping web push setup.")
            return
        }

        val app = if (firebase.apps != undefined && firebase.apps.length > 0) {
            firebase.app()
        } else {
            firebase.initializeApp(config)
        }
        val messaging = firebase.messaging(app)
        messagingInstance = messaging
        initialized = true
        registerServiceWorker(messaging)
    }

    actual fun shouldShowPermissionPrompt(): Boolean {
        if (!isPushSupported()) return false
        window.asDynamic().firebase ?: return false
        window.asDynamic().__FIREBASE_CONFIG ?: return false
        val vapidKey = window.asDynamic().__FIREBASE_VAPID_KEY
        if (vapidKey == undefined || (vapidKey as? String).isNullOrBlank()) return false
        return currentPermission() == "default"
    }

    actual fun requestPermissionFromUserGesture(onResult: (Boolean) -> Unit) {
        if (!isPushSupported()) {
            onResult(false)
            return
        }
        initialize()
        val messaging = messagingInstance ?: run {
            window.asDynamic().console?.warn("Firebase messaging unavailable; cannot request web push token.")
            onResult(false)
            return
        }
        when (val permission = currentPermission()) {
            "granted" -> {
                withServiceWorkerRegistration(
                    onReady = { registration ->
                        requestToken(messaging, registration)
                        onResult(true)
                    },
                    onFailure = { onResult(false) }
                )
            }
            "default" -> {
                window.asDynamic().Notification.requestPermission().then { result: String ->
                    if (result == "granted") {
                        withServiceWorkerRegistration(
                            onReady = { registration ->
                                requestToken(messaging, registration)
                                onResult(true)
                            },
                            onFailure = { onResult(false) }
                        )
                    } else {
                        onResult(false)
                    }
                    null
                }.catch { error: dynamic ->
                    window.asDynamic().console?.error("Failed to request notification permission", error)
                    onResult(false)
                }
            }
            else -> onResult(false)
        }
    }

    private fun registerServiceWorker(messaging: dynamic) {
        val navigator = window.navigator.asDynamic()
        if (navigator.serviceWorker == undefined) {
            window.asDynamic().console?.warn("Service workers unsupported; web push disabled.")
            return
        }
        navigator.serviceWorker.register("/firebase-messaging-sw.js").then { registration: dynamic ->
            serviceWorkerRegistration = registration
            if (messaging.useServiceWorker != undefined) {
                messaging.useServiceWorker(registration)
            }
            listenForTokenRefresh(messaging, registration)
            if (currentPermission() == "granted") {
                requestToken(messaging, registration)
            }
            null
        }.catch { error: dynamic ->
            window.asDynamic().console?.error("Unable to register service worker", error)
        }
    }

    private fun withServiceWorkerRegistration(
        onReady: (dynamic) -> Unit,
        onFailure: (() -> Unit)? = null
    ) {
        val cachedRegistration = serviceWorkerRegistration
        if (cachedRegistration != null) {
            onReady(cachedRegistration)
            return
        }
        val navigator = window.navigator.asDynamic()
        if (navigator.serviceWorker == undefined) {
            window.asDynamic().console?.warn("Service workers unsupported; cannot request web push token.")
            onFailure?.invoke()
            return
        }
        navigator.serviceWorker.ready.then { readyRegistration: dynamic ->
            serviceWorkerRegistration = readyRegistration
            onReady(readyRegistration)
            null
        }.catch { error: dynamic ->
            window.asDynamic().console?.error("Failed to resolve service worker registration", error)
            onFailure?.invoke()
        }
    }

    private fun currentPermission(): String =
        js("Notification.permission") as String

    private fun requestToken(messaging: dynamic, registration: dynamic) {
        val vapidKey = window.asDynamic().__FIREBASE_VAPID_KEY
        if (vapidKey == undefined || (vapidKey as? String).isNullOrBlank()) {
            window.asDynamic().console?.warn("WEB_PUSH_VAPID_KEY missing; cannot request web push token.")
            return
        }
        messaging.getToken(js("{ vapidKey: vapidKey, serviceWorkerRegistration: registration }"))
            .then { token: String? ->
                if (token != null && token.isNotEmpty()) {
                    PushTokenRegistrar.register(
                        token,
                        PushPlatform.WEB,
                        window.navigator.userAgent
                    )
                }
                null
            }.catch { error: dynamic ->
                window.asDynamic().console?.error("Failed to fetch web push token", error)
            }
    }

    private fun listenForTokenRefresh(messaging: dynamic, registration: dynamic) {
        if (messaging.onTokenRefresh != undefined) {
            messaging.onTokenRefresh {
                requestToken(messaging, registration)
            }
        }
    }

    private fun isPushSupported(): Boolean {
        val hasNotification = js("typeof Notification !== 'undefined'") as Boolean
        val hasServiceWorker = js("typeof window !== 'undefined' && 'serviceWorker' in navigator") as Boolean
        return hasNotification && hasServiceWorker
    }
}
